import { observer } from 'mobx-react-lite';
import {
    categoryMenuCreateNewAriaLabel,
    categoryMenuAddCategoryNameText,
    categoryMenuCreateNewText,
} from './NewCategoryMenuItem.locstring.json';
import loc, { format } from 'owa-localize';
import CategoryColorPickerHost from './CategoryColorPickerHost';
import CategoryIcon from './CategoryIcon';
import resetNewCategoryViewState from '../actions/newCategoryMenu/resetNewCategoryViewState';
import setColorPickerSelectedColorId from '../actions/newCategoryMenu/setColorPickerSelectedColorId';
import setNewCategoryErrorText from '../actions/newCategoryMenu/setNewCategoryErrorText';
import setNewCategoryText from '../actions/newCategoryMenu/setNewCategoryText';
import setInitialNewCategoryViewState from '../actions/newCategoryMenu/setInitialNewCategoryViewState';
import categoryStore from '../store/store';
import { DEFAULT_CATEGORY_COLOR_CODE } from '../utils/categoriesConstants';
import CategoryActionSource from '../utils/CategoryActionSource';
import { getCategoryNameErrorText } from '../utils/getCategoryNameErrorText';
import { ITextField } from 'office-ui-fabric-react/lib/components/TextField';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { getRTL } from 'office-ui-fabric-react/lib/Utilities';
import { logUsage } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import getDefaultCategoryColorString from '../utils/getDefaultCategoryColorString';

import * as React from 'react';
import getMasterCategoryList from '../utils/getMasterCategoryList';

const styles = require('./NewCategoryMenuItem.scss');
const classNames = require('classnames/bind').bind(styles);
const createCategoryDatapoint = 'Category_Menu_CreateNewClicked';

export interface NewCategoryMenuItemProps {
    isEditMode: boolean;
    actionSource: CategoryActionSource;
    containerClass?: string;
    showCreateNewCategoryItem?: boolean;
    createNew: (
        name: string,
        selectedColorId: number,
        shouldFavorite: boolean,
        actionSource: CategoryActionSource
    ) => void;
}

export default observer(function NewCategoryMenuItem(props: NewCategoryMenuItemProps) {
    const textField = React.useRef<ITextField>();
    const renderCreateNewMenu = () => {
        const findText = categoryStore.categoryMenuViewState.findText;
        const categoryColorId = getDefaultCategoryColorString();
        const ariaLabel = format(loc(categoryMenuCreateNewAriaLabel), findText);
        return (
            <div
                aria-label={ariaLabel}
                className={classNames(styles.categoryMenuItemContainer, props.containerClass)}
                data-is-focusable={true}
                onKeyDown={onKeyDownOnCreateNewCategory}
                onClick={onCreateNewCategoryClicked}
                role={'menuitem'}>
                {renderCreateNewIcon(categoryColorId)}
                {renderCreateNew(categoryColorId)}
            </div>
        );
    };
    const renderNewCategoryEditMenu = () => {
        const { newCategoryViewState } = categoryStore.categoryMenuViewState;
        const newCategoryInternalContainerClass = classNames(
            styles.newCategoryMenuItemContainerInternal,
            { isInvalid: !!newCategoryViewState.errorText }
        );
        return (
            <div
                className={styles.newCategoryMenuItemContainer}
                data-is-focusable={true}
                role={'menuitem'}>
                <div className={newCategoryInternalContainerClass}>
                    {renderColorPickerControl(newCategoryViewState.selectedColorId)}
                    {renderNewCategoryTextField()}
                </div>
            </div>
        );
    };
    /**
     * Render simple icon for a create new menu
     */
    const renderCreateNewIcon = (categoryColorId: string): JSX.Element => {
        return (
            <CategoryIcon
                categoryColorId={categoryColorId}
                iconClassName={styles.categoryIconInMenu}
                categoryList={getMasterCategoryList()}
            />
        );
    };
    /**
     * Render the color picker control
     */
    const renderColorPickerControl = (categoryColorId: string): JSX.Element => {
        return (
            <CategoryColorPickerHost
                onNewCategoryColorSelected={onNewCategoryColorSelected}
                categoryColorId={categoryColorId}
                colorPickerTagIconClass={styles.colorPickerTagIconClass}
                categoryList={getMasterCategoryList()}
            />
        );
    };
    const onNewCategoryColorSelected = (selectedColorId: string) => {
        setColorPickerSelectedColorId(selectedColorId);
        // Set the focus back in the new text field
        textField.current.focus();
    };
    /**
     * Render the text field to input the new category name
     */
    const renderNewCategoryTextField = (): JSX.Element => {
        return (
            <TextField
                autoFocus={true}
                borderless={true}
                componentRef={newCategoryTextFieldRefCallback}
                className={styles.newCategoryTextField}
                inputClassName={styles.newCategoryTextInput}
                multiline={false}
                onChange={onNewCategoryTextChanged}
                onKeyDown={onKeyDownOnNewTextField}
                placeholder={loc(categoryMenuAddCategoryNameText)}
                value={categoryStore.categoryMenuViewState.newCategoryViewState.categoryText}
            />
        );
    };
    const newCategoryTextFieldRefCallback = (ref: ITextField) => {
        textField.current = ref;
    };
    /**
     * Store text
     */
    const onNewCategoryTextChanged = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        text: string
    ) => {
        setNewCategoryText(text);
    };
    /**
     * Handler key down event on the TextField
     * OnEnter- call commit new category (calls create from props)
     * OnEscape- resetNewCategoryViewState, which will dismiss *only* the new category menu
     * On Left and Right keys - stop propagation so as to not dismiss the menu
     */
    const onKeyDownOnNewTextField = (event: React.KeyboardEvent<unknown>) => {
        switch (event.keyCode) {
            case KeyCodes.left:
                // Stop propagation on left key when user typed in text field to prevent dismiss the category submenu
                if (!getRTL() && textField.current.value) {
                    event.stopPropagation();
                }
                break;
            case KeyCodes.right:
                // Stop propagation on right key for RTL when user typed in text field to prevent dismiss the category submenu
                if (getRTL() && textField.current.value) {
                    event.stopPropagation();
                }
                break;
            case KeyCodes.enter:
                commitNewCategory(
                    textField.current.value.trim(),
                    +categoryStore.categoryMenuViewState.newCategoryViewState.selectedColorId
                );
                break;
            case KeyCodes.escape:
                // Stop propagation so that it does not dismiss the entire menu.
                event.stopPropagation();
                resetNewCategoryViewState();
                break;
        }
    };
    /**
     * Render the category find text and the (Create new) text
     */
    const renderCreateNew = (selectedColorId: string): JSX.Element => {
        const findText = categoryStore.categoryMenuViewState.findText;
        return (
            <div className={styles.createNewTextContainer}>
                <div className={styles.createNewCategoryMatchText}>{'"' + findText + '"'}</div>
                <div className={styles.createNewCategoryText}>
                    &nbsp;{loc(categoryMenuCreateNewText)}
                </div>
            </div>
        );
    };
    /**
     * On key down on create new category menu
     */
    const onKeyDownOnCreateNewCategory = (ev: React.KeyboardEvent) => {
        const findText = categoryStore.categoryMenuViewState.findText;
        const categoryColorId = DEFAULT_CATEGORY_COLOR_CODE.toString();
        if (ev.keyCode == KeyCodes.enter) {
            ev.stopPropagation();
            commitNewCategory(findText, +categoryColorId);
            logUsage(createCategoryDatapoint);
        }
    };
    /**
     * On click on create new category menu
     */
    const onCreateNewCategoryClicked = (ev: React.MouseEvent<unknown>) => {
        const findText = categoryStore.categoryMenuViewState.findText;
        const categoryColorId = DEFAULT_CATEGORY_COLOR_CODE.toString();
        ev.stopPropagation();
        if (isFeatureEnabled('tri-newCategoryCreation')) {
            setInitialNewCategoryViewState(findText);
        } else {
            commitNewCategory(findText, +categoryColorId);
        }
        logUsage(createCategoryDatapoint);
    };
    const commitNewCategory = (categoryName: string, colorId: number) => {
        const errorText = getCategoryNameErrorText(categoryName);
        if (!errorText) {
            props.createNew(categoryName, colorId, false /* shouldFavorite */, props.actionSource);
        } else {
            setNewCategoryErrorText(errorText);
        }
    };
    let { newCategoryViewState } = categoryStore.categoryMenuViewState;
    // Case 1: Show Create New
    // Case 2: Show New Category when in edit mode
    // Resetting newCategoryViewState to null is also a value change and causes
    // re-render. Therefore check for null and early return.
    if (props.isEditMode) {
        if (newCategoryViewState) {
            return renderNewCategoryEditMenu();
        } else {
            return null;
        }
    } else {
        return renderCreateNewMenu();
    }
});
